import { partition, debounce, memoize, round } from 'lodash'

import { bytes } from '@/utils/bytes.js'
import { extension } from '@/utils/extension.js'

class CancellationError extends Error {}

export default {
  props: {
    minSizes: {
      type: Object,
      default: () => ({file_size: 1000000, dimensions: 2000}),
    },
    maxSizes: {
      type: Object,
      default: () => ({file_size: 600000000, resolution: 600000000}),
    },
  },
  computed: {
    imagesIndexed() {
      return this.images?.reduce((acc, cur) => {
        const key = extension(cur.file.name, false)
        acc[key] = cur
        return acc
      }, {})
    },
  },
  watch: {
    // TODO: In Vue 3, watch will not be triggered on array mutation.
    // Migration guide suggests using deep watch, but adding this to
    // the 'images' watcher led to performance problems with thousands of
    // files. Find an alternative so that image replacement still works.
    // https://v3-migration.vuejs.org/breaking-changes/watch.html
    images() { this.debouncedOnImages() },
    // use activeStubs because it changes when either stubs or csvData change
    'activeStubs.length'() { this.debouncedOnImages() },
    minSizes() { this.debouncedOnImages() },
    maxSizes() { this.debouncedOnImages() },
    enableValidation: 'onEnableValidation',
  },
  created() {
    this.debouncedOnImages = debounce(this.onImages, 100)
    this.cachedGetDims = memoize(getDims)
  },
  destroyed() {
    this.cachedGetDims.cache.clear()
  },
  methods: {
    fetchDims(file) {
      if (file.dimensions) { return file.dimensions }
      return this.cachedGetDims(file)
        .then(dimensions => file.dimensions = dimensions)
    },
    async onImages() {
      if (!this.enableValidation) {
        const reset = Object.keys(imageTests).map((k) => [k, undefined])
        this.combinedData.forEach(i => this.setRowErrors(i, reset))
        return
      }
      if (this.stubsLoading) { return }
      this.errorsLoading = true
      try{
        // wait till all images have their dimensions available
        const imagesPromises = this.images.map(i => this.fetchDims(i))
        this.validationPromise = CancellablePromise.all(imagesPromises)
        await this.validationPromise

        this.combinedData.forEach(item => {
          const file = this.imagesIndexed?.[item.image_name]
          this.setRowErrors(item, getImageErrors(file, this.minSizes, this.maxSizes))
        })
      } catch (err) {
        if (err instanceof CancellationError) { return } // ignore errors from cancellation
        throw err
      } finally {
        this.errorsLoading = false
        this.validationPromise = undefined
      }
    },
    onEnableValidation() {
      if (this.validationPromise) {
        this.validationPromise.cancel(new CancellationError('Validation cancelled'))
      } else {
        this.cachedGetDims.cache.clear() // clear validation cache
      }
      this.debouncedOnImages()
    },
    setRowErrors(item, errors) {
      if (!item || !errors.length) { return }

      if (!item.errors) {
        this.$set(item, 'errors', {})
      }

      const [errorsT, errorsF] = partition(errors, ([,v]) => v)
      // set active errors
      errorsT.forEach(([k,v]) => this.$set(item.errors, k, v))
      // remove inactive errors
      errorsF.forEach(([k]) => this.$delete(item.errors, k))

      if (errorsT.length) {
        // highlight row and show details if there are errors
        this.$set(item, '_rowVariant', 'danger')
        this.$set(item, '_showDetails', true)
      } else if (item._rowVariant && !this.hasInvisibleErrors(item.errors)) {
        // remove row highlight and hide details if there are no errors, and
        // no other "invisible" errors (errors on fields without a visible column)
        this.$set(item, '_rowVariant', undefined)
        this.$set(item, '_showDetails', false)
      }
    },
  },
}

const imageTests = {
  file_size(file, min, max) {
    if (file?.file?.size < min) {
      return `${bytes(file.file.size)} is less than the minimum size, ${bytes(min)}.`
    }
    if (file?.file?.size > max) {
      return `${bytes(file.file.size)} is greater than the maximum size, ${bytes(max)}.`
    }
  },
  dimensions(file, minLength) {
    const dimensions = file?.dimensions
    if (dimensions?.some(i => i < minLength)) {
      return `${fmtDims(dimensions)} px is smaller than minimum size, ${fmtDims([minLength, minLength])} px.`
    }
  },
  resolution(file, min, max) {
    const dimensions = file?.dimensions
    if (file && !dimensions && max) {
      return `Error getting dimensions. The maximum size is ${fmtMP(max)} Megapixels. Please contact support with any questions.`
    }
    if (!dimensions) { return }
    const pixels = dimensions[0] * dimensions[1]
    if (pixels < min) {
      return `${fmtMP(pixels)} Megapixels is smaller than the minimum size, ${fmtMP(min)} MP.`
    }
    if (pixels > max) {
      return `${fmtMP(pixels)} Megapixels is larger than the maximum size, ${fmtMP(max)} MP.`
    }
  },
}

function getImageErrors(file, minSizes, maxSizes, tests = imageTests) {
  return Object.entries(tests)
    .map(([k,fn]) => [k, fn(file, minSizes?.[k], maxSizes?.[k])])
}

// from https://stackoverflow.com/a/64155813/490592
class CancellablePromise extends Promise {
  constructor(executor) {
    let cancel = null
    super((resolve, reject) => {
      cancel = reject
      executor(resolve, reject)
    })
    this.cancel = cancel
  }

  then(onFulfilled, onRejected) {
    const p = super.then(onFulfilled, onRejected)
    p.cancel = this.cancel
    return p
  }

  static all(cancellablePromises) {
    const promise = new this((resolve, reject) => {
      Promise.all(cancellablePromises).then(resolve, reject)
    })
    const cancelOriginal = promise.cancel
    promise.cancel = (err) => {
      cancellablePromises.forEach(i => i.cancel?.(err))
      cancelOriginal(err)
    }
    return promise
  }
}

function getDims(file) {
  // get image dimensions (async)
  if (file?.blob) {
    const image = new Image()
    return new CancellablePromise((resolve, reject) => {
      image.onload = () => {
        resolve([image.width, image.height])
      }
      image.onerror = reject
      image.src = file.blob
    })
    .catch((err) => {
      if (err instanceof CancellationError) {
        image.src = '' // tells browser to stop loading
        return
      }
      console.error(`Error getting dimensions ${file.name}`)
    })
  }
}

function fmtDims(d) {
  return d.map(i => Number(i).toLocaleString()).join('×')
}
function fmtMP(i, precision = 1) {
  // px to MP
  i = i / 1000000
  // round to one decimal place
  i = round(i, precision)
  // add punctuation
  i = Number(i).toLocaleString()
  return i
}
